import { useRef, useEffect, useState, useMemo } from "react";
import { Rect, Text, Group } from "react-konva";
import { Html } from "react-konva-utils";
import { applyTransparency } from "../../../SectionElements/plugins/utils";
import {
  checkSimpleHitDetection,
  checkAdvancedHitDetection,
} from "../../../SectionElements/utils/pixelHitDetection";
import { createHitDetectionVisualization } from "../../../SectionElements/utils/debugHelper";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import {
  getAbsoluteHorizontalExtent,
  getOffCanvasMainColumnBounds,
  getOffCanvasHorizontalStripSide,
  shiftXToStayInOffCanvasStrip,
} from "../../utils/boundaryUtils";
import { constrainOffCanvasPosition } from "../../utils/dragEndUtils";
import { getOffCanvasStageWidthPx } from "../../utils/mainCanvasLayout";

const RectangleElement = ({
  rectangle,
  strokeColor,
  isDraggable = true,
  onClick,
  onDragEnd,
  onDragMove,
  onTransformEnd,
  onTransform,
  canvasHeight,
  canvasWidth = window.innerWidth * 0.45,
  canvasResizeTrigger = 0,
  images = [], // Array of images for pixel-level hit detection
  stageRef = null, // Stage reference for hit detection
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
  onImageClick = null, // Callback for when an underlying image should be selected
  effectiveZoomLevel = 1, // Zoom level for scaling elements
  isSelected = false,
  isOffCanvas = false,
  isPreview = false,
  onCopyToOppositeCanvas,
  selectedObject = null,
  selectedObjects = [],
  /** When true, skip hit detection / onImageClick / selection side effects (clone mode & locked tools). */
  ignoreClickSelection = false,
}) => {
  const isRectTransformClampDebugEnabled = (() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debugRectTransformClamp") === "1"
      );
    } catch {
      return false;
    }
  })();
  const shapeRef = useRef();
  const textGroupRef = useRef();
  /** "left" | "right" | null — locked for the current off-canvas drag so the rect cannot cross strips */
  const offCanvasDragStripSideRef = useRef(null);
  const justDoubleClickedRef = useRef(false);
  const doubleClickOriginalPositionRef = useRef({ x: null, y: null });
  const transformLastValidRef = useRef(null);
  const transformActiveAnchorRef = useRef(null);

  const MIN_WIDTH = 10;
  const MIN_HEIGHT = 0;
  const allowDrag = isDraggable && !rectangle.isNew;
  const isDrawing = rectangle.isNew;
  const PADDING = 4;
  // Keep a small inset so transformer anchors stay visible when selected.
  const TRANSFORMER_ANCHOR_BUFFER_PX = 10;

  // Toolbar boundary constant - prevents elements from going behind toolbar
  // Toolbar is positioned at left: 8px with padding: 5px and buttons: 28px wide
  // Total width: 8px (left) + 5px (padding) + 28px (button) + 5px (padding) = 46px
  // Using 55px to account for toolbar area, border-radius, box-shadow, and safety margin
  const TOOLBAR_WIDTH = 55;

  // State to force re-render when canvas is resized or font size changes
  const [renderKey, setRenderKey] = useState(0);

  // Calculate canvas boundaries for clipping (recalculated on each render)
  const canvasPixelHeight =
    canvasHeight || (parseInt(canvasHeight) / 100) * window.innerHeight;
  const canvasPixelWidth = isOffCanvas
    ? canvasWidth || getOffCanvasStageWidthPx()
    : canvasWidth || window.innerWidth * 0.45;

  // Resize handle constants (must match Canvas.jsx)
  const RESIZE_HANDLE_HEIGHT = 10;
  const RESIZE_BOTTOM_SAFE = 0;
  // Effective canvas height for text clipping (subtract resize handle area)
  const effectiveCanvasHeight = Math.max(
    0,
    canvasPixelHeight - RESIZE_HANDLE_HEIGHT - RESIZE_BOTTOM_SAFE
  );

  const MAX_WIDTH = Math.max(
    MIN_WIDTH,
    Number(canvasPixelWidth) || window.innerWidth * 0.45,
  );
  const MAX_HEIGHT = Math.min(
    600,
    Math.max(MIN_HEIGHT, effectiveCanvasHeight || 600),
  );

  const {
    text: textContent = "",
    fontSize = 15,
    textColor = "black",
    fontStyle = "normal",
    textDecoration = "",
    align = "center",
    verticalAlign = "middle",
    rotation = 0,
  } = rectangle;

  // Force re-render when canvas is resized or font size changes
  // Must be after fontSize is defined
  useEffect(() => {
    // Force a re-render to recalculate text dimensions, line count, and clipping
    setRenderKey((prev) => prev + 1);
  }, [canvasResizeTrigger, canvasHeight, fontSize]);

  // Update Konva shape directly when strokeWidth changes (e.g., during undo/redo)
  useEffect(() => {
    if (shapeRef.current) {
      const baseStrokeWidth = rectangle.strokeWidth || 2;
      let effectiveStrokeWidth;
      if (effectiveZoomLevel && effectiveZoomLevel < 0.5) {
        effectiveStrokeWidth = Math.max(
          1,
          baseStrokeWidth / Math.max(0.1, effectiveZoomLevel)
        );
      } else {
        effectiveStrokeWidth = Math.max(1, baseStrokeWidth);
      }
      shapeRef.current.strokeWidth(effectiveStrokeWidth);
      shapeRef.current.getLayer()?.batchDraw();
    }
  }, [rectangle.strokeWidth, effectiveZoomLevel]);

  // Track if we're in a group drag to prevent HTML re-renders
  const isInGroupDragRef = useRef(false);

  // CRITICAL PERFORMANCE FIX: Cache HTML content during group drag to prevent expensive re-renders
  // The HTML content is expensive to render, especially with lots of formatting
  // During group drag, we only update Konva node positions, not the HTML content
  const htmlContentRef = useRef(textContent || "");

  // Update shape and text Group position during group drag using requestAnimationFrame for smooth movement
  // CRITICAL PERFORMANCE FIX: This effect only updates Konva node positions, NOT React state or HTML content
  // This prevents expensive HTML re-renders during drag move
  useEffect(() => {
    const isInGroupDrag = selectedObjects && selectedObjects.length > 1 &&
      selectedObjects.some((obj) => obj.id === rectangle.id && obj.type === "Rect");

    // Update ref to track group drag state (used to prevent HTML re-renders)
    isInGroupDragRef.current = isInGroupDrag;

    // Cache HTML content when entering group drag
    if (isInGroupDrag && htmlContentRef.current !== textContent) {
      htmlContentRef.current = textContent || "";
    }

    if (!isInGroupDrag || !textGroupRef.current || !shapeRef.current) {
      return;
    }

    let animationFrameId;
    let lastX = null;
    let lastY = null;
    let lastRotation = null;

    const updatePositions = () => {
      if (textGroupRef.current && shapeRef.current) {
        try {
          // Get current position from shape node (updated by handleGroupDragMove)
          const currentX = shapeRef.current.x();
          const currentY = shapeRef.current.y();
          const currentRotation = shapeRef.current.rotation();

          // Only update if position changed (avoid unnecessary updates)
          if (currentX !== lastX || currentY !== lastY || currentRotation !== lastRotation) {
            lastX = currentX;
            lastY = currentY;
            lastRotation = currentRotation;

            // Recalculate text dimensions (same logic as getTextContainerDimensions)
            const baseStrokeWidth = rectangle.strokeWidth || 2;
            let effectiveStrokeWidth;
            if (effectiveZoomLevel && effectiveZoomLevel < 0.5) {
              effectiveStrokeWidth = Math.max(
                1,
                baseStrokeWidth / Math.max(0.1, effectiveZoomLevel)
              );
            } else {
              effectiveStrokeWidth = Math.max(1, baseStrokeWidth);
            }
            const strokePadding = Math.ceil(effectiveStrokeWidth / 2);

            // CRITICAL FIX: Update both Konva Group position AND HTML container position for smooth text movement
            // This prevents text lag during drag by keeping HTML in sync with shape
            textGroupRef.current.x(currentX + strokePadding);
            textGroupRef.current.y(currentY + strokePadding);
            textGroupRef.current.rotation(currentRotation);

            // CRITICAL: Update HTML container position directly for smooth text movement
            // This ensures the HTML text follows the shape instantly during drag
            const htmlContainer = textGroupRef.current.getLayer()?.canvas?.container?.querySelector('.konvajs-content');
            if (htmlContainer) {
              const htmlElements = htmlContainer.querySelectorAll('[data-rectangle-id="' + rectangle.id + '"]');
              htmlElements.forEach(htmlEl => {
                if (htmlEl && htmlEl.style) {
                  // Get stage position to convert coordinates
                  const stage = shapeRef.current.getStage();
                  if (stage) {
                    const stageContainer = stage.container();
                    const stageRect = stageContainer.getBoundingClientRect();

                    // Calculate absolute position for HTML element
                    const absoluteX = stageRect.left + currentX + strokePadding;
                    const absoluteY = stageRect.top + currentY + strokePadding;

                    htmlEl.style.transform = `translate(${absoluteX}px, ${absoluteY}px) rotate(${currentRotation}deg)`;
                    htmlEl.style.position = 'fixed';
                    htmlEl.style.zIndex = isSelected ? '15' : '3';
                  }
                }
              });
            }

            // Force layer redraw for smooth updates (only Konva, no React re-render)
            const layer = textGroupRef.current.getLayer();
            if (layer) {
              layer.batchDraw();
            }

            // Also ensure shape layer is drawn (for stroke updates)
            const shapeLayer = shapeRef.current.getLayer();
            if (shapeLayer && shapeLayer !== layer) {
              shapeLayer.batchDraw();
            }
          }
        } catch (e) {
          // Ignore errors during drag
        }
      }
      animationFrameId = requestAnimationFrame(updatePositions);
    };

    animationFrameId = requestAnimationFrame(updatePositions);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      // Reset ref when effect cleans up
      isInGroupDragRef.current = false;
    };
  }, [selectedObjects, rectangle.id, rectangle.width, rectangle.height, rectangle.strokeWidth, effectiveZoomLevel]);

  const parsedFontStyle = (() => {
    const isItalic = fontStyle.includes("italic");
    const isBold = fontStyle.includes("bold");
    if (isItalic && isBold) return "italic bold";
    if (isItalic) return "italic";
    if (isBold) return "bold";
    return "normal";
  })();

  // Function to parse HTML-like formatting
  const parseFormattedText = (text) => {
    if (!text) return text;

    // Remove HTML-like formatting for display
    let cleanText = text
      .replace(/<bold>(.*?)<\/bold>/g, "$1") // Remove bold tags
      .replace(/<italic>(.*?)<\/italic>/g, "$1") // Remove italic tags
      .replace(/<underline>(.*?)<\/underline>/g, "$1") // Remove underline tags
      .replace(/<highlight>(.*?)<\/highlight>/g, "$1") // Remove highlight tags
      .replace(/<color[^>]*>(.*?)<\/color>/g, "$1") // Remove color tags
      .replace(/<fontsize[^>]*>(.*?)<\/fontsize>/g, "$1"); // Remove font size tags

    return cleanText;
  };

  const displayText = parseFormattedText(textContent);

  // Check if rectangle element is completely outside canvas boundaries
  const isOutsideCanvas = () => {
    const elementRight = rectangle.x + rectangle.width;
    const elementBottom = rectangle.y + rectangle.height;

    return (
      rectangle.x >= canvasPixelWidth || // Completely to the right
      rectangle.y >= canvasPixelHeight || // Completely below
      elementRight <= 0 || // Completely to the left
      elementBottom <= 0 // Completely above
    );
  };

  // Calculate the correct position based on drawing state
  const getCorrectPosition = () => {
    // Always treat x/y as top-left corner
    return { x: rectangle.x, y: rectangle.y };
  };

  // Calculate stroke width accounting for zoom level
  // Use the actual strokeWidth from rectangle, with zoom compensation only if needed
  const getEffectiveStrokeWidth = () => {
    const baseStrokeWidth = rectangle.strokeWidth || 2;
    // Only apply zoom compensation if zoom level is very low (< 0.5)
    // Otherwise, use the actual strokeWidth value
    if (effectiveZoomLevel && effectiveZoomLevel < 0.5) {
      return Math.max(1, baseStrokeWidth / Math.max(0.1, effectiveZoomLevel));
    }
    return Math.max(1, baseStrokeWidth);
  };

  const normalizeRotationDeg = (deg) => {
    let next = Number(deg) || 0;
    next %= 360;
    if (next < 0) next += 360;
    return next;
  };

  const getEffectiveInset = (requestedInset, stageSize, rectSize) => {
    const safeInset = Math.max(0, Number(requestedInset) || 0);
    const safeStage = Math.max(0, Number(stageSize) || 0);
    const safeRect = Math.max(0, Number(rectSize) || 0);
    return Math.max(0, Math.min(safeInset, (safeStage - safeRect) / 2));
  };

  const isClientRectInsideStage = (
    rect,
    stageWidth,
    stageHeight,
    inset = 0,
    eps = 0.5
  ) => {
    if (!rect) return true;
    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const insetY = getEffectiveInset(inset, stageHeight, rect.height);
    return (
      rect.x >= insetX - eps &&
      rect.y >= insetY - eps &&
      rect.x + rect.width <= stageWidth - insetX + eps &&
      rect.y + rect.height <= stageHeight - insetY + eps
    );
  };

  // Keep the transformed bbox inside stage by shifting node x/y once, after transform ends.
  const clampNodePositionInsideStageByClientRect = (
    node,
    stageWidth,
    stageHeight,
    inset = 0
  ) => {
    if (!node) return { x: 0, y: 0 };
    const stage = node.getStage?.();
    // Multi-pass clamp:
    // After the first shift, Konva's client-rect can change slightly (stroke/buffer/rotation),
    // so we iterate a few times to guarantee the final bbox stays in-bounds.
    for (let pass = 0; pass < 8; pass += 1) {
      const rect = stage
        ? node.getClientRect({ relativeTo: stage })
        : node.getClientRect();
      const insetX = getEffectiveInset(inset, stageWidth, rect.width);
      const insetY = getEffectiveInset(inset, stageHeight, rect.height);
      let dx = 0;
      let dy = 0;

      if (rect.x < insetX) dx = insetX - rect.x;
      else if (rect.x + rect.width > stageWidth - insetX) {
        dx = stageWidth - insetX - (rect.x + rect.width);
      }

      if (rect.y < insetY) dy = insetY - rect.y;
      else if (rect.y + rect.height > stageHeight - insetY) {
        dy = stageHeight - insetY - (rect.y + rect.height);
      }

      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
        break;
      }

      node.x((Number(node.x()) || 0) + dx);
      node.y((Number(node.y()) || 0) + dy);
      // Ensure client rect reflects the latest x/y before next pass.
      node.getLayer?.()?.batchDraw?.();
    }

    return { x: node.x(), y: node.y() };
  };

  // Clamp rotation at transform-end so rotated bbox stays within stage.
  const clampRotationToStayWithinStage = (
    node,
    desiredRotation,
    stageWidth,
    stageHeight,
    inset = 0
  ) => {
    if (!node) return normalizeRotationDeg(desiredRotation);
    const stage = node.getStage?.();
    const base = normalizeRotationDeg(desiredRotation);
    const tryRotation = (candidate) => {
      node.rotation(candidate);
      return isClientRectInsideStage(
        stage ? node.getClientRect({ relativeTo: stage }) : node.getClientRect(),
        stageWidth,
        stageHeight,
        inset
      );
    };
    if (tryRotation(base)) return base;
    for (let step = 1; step <= 180; step += 1) {
      const left = normalizeRotationDeg(base - step);
      if (tryRotation(left)) return left;
      const right = normalizeRotationDeg(base + step);
      if (tryRotation(right)) return right;
    }
    node.rotation(base);
    return base;
  };

  const clampNodeInsideOffCanvasStripsByClientRect = (
    node,
    stageWidth,
    stageHeight,
    inset = 0,
    options = {}
  ) => {
    if (!node || stageWidth <= 0 || stageHeight <= 0) {
      return { x: node?.x?.() ?? 0, y: node?.y?.() ?? 0 };
    }
    const clampXEnabled = options.clampX !== false;
    const clampYEnabled = options.clampY !== false;
    const stage = node.getStage?.();
    const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
    for (let pass = 0; pass < 8; pass += 1) {
      const rect = stage
        ? node.getClientRect({ relativeTo: stage })
        : node.getClientRect();
      let dx = 0;
      let dy = 0;

      const insetX = getEffectiveInset(inset, stageWidth, rect.width);
      const insetY = getEffectiveInset(inset, stageHeight, rect.height);
      if (clampXEnabled) {
        if (rect.x < insetX) dx += insetX - rect.x;
        else if (rect.x + rect.width > stageWidth - insetX) {
          dx += stageWidth - insetX - (rect.x + rect.width);
        }
      }
      if (clampYEnabled) {
        if (rect.y < insetY) dy += insetY - rect.y;
        else if (rect.y + rect.height > stageHeight - insetY) {
          dy += stageHeight - insetY - (rect.y + rect.height);
        }
      }

      const overlapsMainColumn =
        rect.x < mainZoneEnd && rect.x + rect.width > mainZoneStart;
      if (overlapsMainColumn) {
        // For off-canvas rectangles we always want the resolved placement
        // to end up in the left strip (never on the right side).
        dx += mainZoneStart - (rect.x + rect.width);
      }

      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) break;
      node.x((Number(node.x()) || 0) + dx);
      node.y((Number(node.y()) || 0) + dy);
      // Ensure client rect updates before next pass.
      node.getLayer?.()?.batchDraw?.();
    }
    return { x: node.x(), y: node.y() };
  };

  const isOffCanvasClientRectValid = (
    rect,
    stageWidth,
    stageHeight,
    inset = 0,
    eps = 0.5
  ) => {
    if (!rect) return true;
    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const insetY = getEffectiveInset(inset, stageHeight, rect.height);
    const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
    const insideStage =
      rect.x >= insetX - eps &&
      rect.y >= insetY - eps &&
      rect.x + rect.width <= stageWidth - insetX + eps &&
      rect.y + rect.height <= stageHeight - insetY + eps;
    const overlapsMainColumn =
      rect.x < mainZoneEnd - eps && rect.x + rect.width > mainZoneStart + eps;
    return insideStage && !overlapsMainColumn;
  };

  const isOffCanvasClientRectXValid = (
    rect,
    stageWidth,
    inset = 0,
    eps = 0.5
  ) => {
    if (!rect) return true;
    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
    const insideX =
      rect.x >= insetX - eps &&
      rect.x + rect.width <= stageWidth - insetX + eps;
    const overlapsMainColumn =
      rect.x < mainZoneEnd - eps && rect.x + rect.width > mainZoneStart + eps;
    return insideX && !overlapsMainColumn;
  };

  // Calculate text container dimensions accounting for stroke width
  const getTextContainerDimensions = () => {
    const strokeWidth = getEffectiveStrokeWidth();
    const strokePadding = Math.ceil(strokeWidth / 2); // Half stroke width on each side

    // Calculate available space after accounting for stroke
    const availableWidth = Math.max(0, rectangle.width - strokePadding * 2);
    const availableHeight = Math.max(0, rectangle.height - strokePadding * 2);

    // Use a smaller container size to leave white space for dragging (similar to CircleElement)
    const containerWidth = Math.max(0, availableWidth * 0.95); // 95% of available width for less spacing
    const containerHeight = Math.max(0, availableHeight * 0.95); // 95% of available height for less spacing

    return {
      width: containerWidth,
      height: containerHeight,
      padding: strokePadding,
    };
  };

  const correctPosition = getCorrectPosition();

  const rectX = rectangle.x ?? 0;
  const rectY = rectangle.y ?? 0;
  const rectWidth = rectangle.width ?? 0;
  const shouldShowCopyButton =
    isSelected &&
    !isPreview &&
    !isDrawing &&
    isOffCanvas && // Only show copy button for off-canvas rectangles
    typeof onCopyToOppositeCanvas === "function";

  const copyButtonLeft = (() => {
    const desiredLeft = rectX + rectWidth - 28;
    const minLeft = 4;
    const maxLeft = Math.max(canvasPixelWidth - 28, minLeft);
    return Math.max(minLeft, Math.min(desiredLeft, maxLeft));
  })();

  const copyButtonTop = (() => {
    const desiredTop = rectY - 36;
    const minTop = 4;
    const maxTop = Math.max(effectiveCanvasHeight - 28, minTop);
    return Math.max(minTop, Math.min(desiredTop, maxTop));
  })();

  const handleCopyClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onCopyToOppositeCanvas?.();
  };

  // Check if this element's HTML content is behind any selected element
  const isHtmlContentBehindSelected = () => {
    // If this element is selected, don't hide its HTML content
    if (isSelected) {
      return false;
    }

    // Get all selected elements (from selectedObjects or selectedObject)
    const allSelected = [];
    if (selectedObjects && selectedObjects.length > 0) {
      allSelected.push(...selectedObjects);
    } else if (selectedObject) {
      allSelected.push(selectedObject);
    }

    if (allSelected.length === 0) {
      return false;
    }

    // Get this element's bounding box
    const correctPos = getCorrectPosition();
    const thisBBox = {
      x: correctPos.x,
      y: correctPos.y,
      width: rectangle.width || 0,
      height: rectangle.height || 0,
    };

    // Check if any selected element overlaps with this element
    return allSelected.some((selected) => {
      // Skip if it's the same element
      if (selected.id === rectangle.id) {
        return false;
      }

      // Get selected element's bounding box
      let selectedBBox = null;
      if (selected.type === "Rect") {
        selectedBBox = {
          x: selected.x || 0,
          y: selected.y || 0,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      } else if (selected.type === "Circle") {
        const radius = selected.radius || Math.max(selected.radiusX || 0, selected.radiusY || 0);
        selectedBBox = {
          x: (selected.x || 0) - radius,
          y: (selected.y || 0) - radius,
          width: radius * 2,
          height: radius * 2,
        };
      } else if (selected.type === "Text" || selected.type?.includes("Text")) {
        selectedBBox = {
          x: selected.x || 0,
          y: selected.y || 0,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      } else if (selected.type === "Image") {
        selectedBBox = {
          x: (selected.x || 0) - (selected.width || 0) / 2,
          y: (selected.y || 0) - (selected.height || 0) / 2,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      }

      if (!selectedBBox) {
        return false;
      }

      // Check for overlap (simple rectangle overlap check)
      const hasOverlap = !(
        thisBBox.x + thisBBox.width < selectedBBox.x ||
        thisBBox.x > selectedBBox.x + selectedBBox.width ||
        thisBBox.y + thisBBox.height < selectedBBox.y ||
        thisBBox.y > selectedBBox.y + selectedBBox.height
      );

      return hasOverlap;
    });
  };

  // Calculate mask-image to hide only the overlapping portion
  // Returns an object with mask-image properties (not used, kept for compatibility)
  // The actual clipping is done via clip-path on the inner div
  const getHtmlMask = () => {
    // Don't apply any mask here - we use clip-path on the inner div instead
    // This prevents issues with mask-composite browser compatibility
    return { maskImage: "none", WebkitMaskImage: "none" };
  };

  // Calculate clip-path to hide only the overlapping portion (kept for backward compatibility)
  const getHtmlClipPath = () => {
    // If this element is selected, don't clip
    if (isSelected) {
      return "none";
    }

    // Get all selected elements (from selectedObjects or selectedObject)
    const allSelected = [];
    if (selectedObjects && selectedObjects.length > 0) {
      allSelected.push(...selectedObjects);
    } else if (selectedObject) {
      allSelected.push(selectedObject);
    }

    if (allSelected.length === 0) {
      return "none";
    }

    // Get the text container dimensions and position
    const textDimensions = getTextContainerDimensions();
    const correctPos = getCorrectPosition();

    // Calculate actual rendered dimensions (accounting for canvas boundaries)
    const rectLeft = correctPos.x + textDimensions.padding;
    const rectTop = correctPos.y + textDimensions.padding;
    const availableWidth = canvasPixelWidth - rectLeft;
    const actualDivWidth = Math.max(0, Math.min(textDimensions.width, availableWidth));

    const rectBottom = rectTop + textDimensions.height;
    let actualDivHeight = textDimensions.height;
    if (rectTop >= effectiveCanvasHeight || rectBottom <= 0) {
      actualDivHeight = 0;
    } else {
      const visibleTop = Math.max(0, rectTop);
      const visibleBottom = Math.min(effectiveCanvasHeight, rectBottom);
      const visibleHeight = visibleBottom - visibleTop;
      actualDivHeight = Math.max(0, visibleHeight);
    }

    // Text container bounds in stage coordinates
    const textContainerBBox = {
      x: rectLeft,
      y: rectTop,
      width: actualDivWidth > 0 ? actualDivWidth : textDimensions.width,
      height: actualDivHeight > 0 ? actualDivHeight : textDimensions.height,
    };

    // Collect all overlapping areas
    const clipAreas = [];

    allSelected.forEach((selected) => {
      if (selected.id === rectangle.id) {
        return;
      }

      // Get selected element's bounding box
      let selectedBBox = null;
      if (selected.type === "Rect") {
        selectedBBox = {
          x: selected.x || 0,
          y: selected.y || 0,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      } else if (selected.type === "Circle") {
        const selectedRadius = selected.radius || Math.max(selected.radiusX || 0, selected.radiusY || 0);
        selectedBBox = {
          x: (selected.x || 0) - selectedRadius,
          y: (selected.y || 0) - selectedRadius,
          width: selectedRadius * 2,
          height: selectedRadius * 2,
        };
      } else if (selected.type === "Text" || selected.type?.includes("Text")) {
        selectedBBox = {
          x: selected.x || 0,
          y: selected.y || 0,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      } else if (selected.type === "Image") {
        selectedBBox = {
          x: (selected.x || 0) - (selected.width || 0) / 2,
          y: (selected.y || 0) - (selected.height || 0) / 2,
          width: selected.width || 0,
          height: selected.height || 0,
        };
      }

      if (!selectedBBox) {
        return;
      }

      // Calculate intersection (overlapping area)
      const intersectLeft = Math.max(textContainerBBox.x, selectedBBox.x);
      const intersectTop = Math.max(textContainerBBox.y, selectedBBox.y);
      const intersectRight = Math.min(textContainerBBox.x + textContainerBBox.width, selectedBBox.x + selectedBBox.width);
      const intersectBottom = Math.min(textContainerBBox.y + textContainerBBox.height, selectedBBox.y + selectedBBox.height);

      // If there's an intersection, add it to clip areas
      if (intersectLeft < intersectRight && intersectTop < intersectBottom) {
        // Convert to relative coordinates (relative to text container's position)
        const relativeLeft = intersectLeft - textContainerBBox.x;
        const relativeTop = intersectTop - textContainerBBox.y;
        const relativeRight = intersectRight - textContainerBBox.x;
        const relativeBottom = intersectBottom - textContainerBBox.y;

        clipAreas.push({
          left: relativeLeft,
          top: relativeTop,
          right: relativeRight,
          bottom: relativeBottom,
        });
      }
    });

    // If no overlapping areas, don't clip
    if (clipAreas.length === 0) {
      return "none";
    }

    // Handle single overlapping area
    if (clipAreas.length === 1) {
      const area = clipAreas[0];
      const leftPercent = Math.max(0, (area.left / textContainerBBox.width) * 100);
      const rightPercent = Math.min(100, (area.right / textContainerBBox.width) * 100);
      const topPercent = Math.max(0, (area.top / textContainerBBox.height) * 100);
      const bottomPercent = Math.min(100, (area.bottom / textContainerBBox.height) * 100);

      // Use the same polygon approach as TextElement
      const polygon = [
        `0% 0%`,
        `${leftPercent}% 0%`,
        `${leftPercent}% ${topPercent}%`,
        `${rightPercent}% ${topPercent}%`,
        `${rightPercent}% 0%`,
        `100% 0%`,
        `100% 100%`,
        `${rightPercent}% 100%`,
        `${rightPercent}% ${bottomPercent}%`,
        `${leftPercent}% ${bottomPercent}%`,
        `${leftPercent}% 100%`,
        `0% 100%`,
        `0% 0%`,
      ].join(", ");

      return `polygon(${polygon})`;
    }

    // For multiple overlapping areas, don't clip
    return "none";
  };

  // Get visibility style for HTML content (for backward compatibility, but we'll use clip-path instead)
  const getHtmlVisibility = () => {
    // Don't use visibility anymore - use clip-path instead
    return "visible";
  };

  // Get opacity for HTML content - reduce opacity when another element is selected
  const getHtmlOpacity = () => {
    // Check if this element is in selectedObjects array
    const isInSelectedObjects = selectedObjects && selectedObjects.some(
      (obj) => obj.id === rectangle.id && obj.type === "Rect"
    );

    // If this element is selected (either via isSelected prop or in selectedObjects), keep full opacity
    if (isSelected || isInSelectedObjects) {
      return rectangle.opacity !== undefined ? rectangle.opacity : 1;
    }

    // Get all selected elements (from selectedObjects or selectedObject)
    const allSelected = [];
    if (selectedObjects && selectedObjects.length > 0) {
      allSelected.push(...selectedObjects);
    } else if (selectedObject) {
      allSelected.push(selectedObject);
    }

    // If nothing is selected, keep full opacity
    if (allSelected.length === 0) {
      return rectangle.opacity !== undefined ? rectangle.opacity : 1;
    }

    // Otherwise, reduce opacity for non-selected elements when something else is selected
    const baseOpacity = rectangle.opacity !== undefined ? rectangle.opacity : 1;
    return baseOpacity * 0.3;
  };

  // Don't unmount an actively selected rectangle during transform/rotate.
  // Unmounting while Transformer still references this node can trigger
  // "Cannot read properties of null (reading 'getAbsoluteTransform')".
  if (isOutsideCanvas() && !isSelected) {
    return null;
  }

  return (
    <>
      {/* Base rectangle */}
      <Rect
        name={rectangle.name}
        rotation={rotation}
        ref={shapeRef}
        id={rectangle.id}
        x={correctPosition.x}
        y={correctPosition.y}
        width={rectangle.width}
        height={rectangle.height}
        scaleX={1}
        scaleY={1}
        offsetX={0}
        offsetY={0}
        dragDistance={10}
        draggable={allowDrag}
        onClick={(e) => {
          if (ignoreClickSelection) {
            return;
          }
          if (!isPrimaryMouseButton(e.evt)) {
            return;
          }
          // Get the click position relative to the stage
          const stage = e.target.getStage();
          const pointerPosition = stage.getPointerPosition();


          // Create visualization for debugging
          createHitDetectionVisualization(
            pointerPosition,
            { ...rectangle, type: "Rect" },
            images
          );

          // Use advanced hit detection to distinguish between stroke and fill areas
          const hitResult = checkAdvancedHitDetection(
            pointerPosition,
            { ...rectangle, type: "Rect", effectiveZoomLevel },
            images
          );


          if (
            hitResult.shouldSelectImage &&
            hitResult.selectedImage &&
            onImageClick
          ) {
            onImageClick(hitResult.selectedImage);
            return;
          }

          if (hitResult.shouldSelectShape && onClick) {
            // Select the rectangle
            e.evt?.stopPropagation?.(); // Prevent event bubbling
            e.evt?.preventDefault?.(); // Prevent default behavior

            // Mark this as a stroke click ONLY if the hit detection actually detected a stroke click
            // This prevents false positives when clicking on fill area
            if (e.target && e.target.attrs && hitResult.isOnStroke === true) {
              e.target.attrs.isStrokeClick = true;
            } else if (e.target && e.target.attrs) {
              // Clear the flag if it was previously set but this is not a stroke click
              e.target.attrs.isStrokeClick = false;
            }

            onClick(e);
            return;
          }

          // Fallback to default behavior
          if (onClick) {
            onClick(e);
          }
        }}
        onDblClick={(e) => {
          if (ignoreClickSelection) {
            return;
          }
          // Treat double-click as single click operation only
          // Prevent drag from starting after double-click
          e.evt?.stopPropagation?.();
          e.evt?.preventDefault?.();

          const node = shapeRef.current;
          if (node) {
            // Store original position to reset if drag starts
            doubleClickOriginalPositionRef.current = {
              x: node.x(),
              y: node.y()
            };

            // Immediately stop any ongoing drag
            node.stopDrag();

            // Disable dragging temporarily
            node.setDraggable(false);

            // Set flag to prevent drag from starting
            justDoubleClickedRef.current = true;

            // Re-enable dragging and clear flag after a delay
            setTimeout(() => {
              justDoubleClickedRef.current = false;
              doubleClickOriginalPositionRef.current = { x: null, y: null };
              if (node && allowDrag) {
                node.setDraggable(true);
              }
            }, 500);
          }

          // Call onClick handler to treat double-click as single click
          if (onClick) {
            onClick(e);
          }
        }}
        onDragStart={(e) => {
          // CRITICAL SAFETY CHECK: Verify mouse button is actually pressed
          if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
            const node = e.target;
            if (node) {
              node.stopDrag();
            }
            return;
          }

          // Prevent drag from starting immediately after a double-click
          if (justDoubleClickedRef.current) {
            e.evt?.preventDefault?.();
            e.evt?.stopPropagation?.();
            const node = e.target;
            if (node) {
              // Stop the drag immediately
              node.stopDrag();
              // Reset position if we have it stored
              if (doubleClickOriginalPositionRef.current.x !== null) {
                node.x(doubleClickOriginalPositionRef.current.x);
                node.y(doubleClickOriginalPositionRef.current.y);
              }
              // Disable dragging
              node.setDraggable(false);
              // Re-enable after delay
              setTimeout(() => {
                if (node && allowDrag) {
                  node.setDraggable(true);
                }
              }, 100);
            }
            return;
          }

          // CRITICAL FIX: Initialize group drag immediately when drag starts if in multi-selection
          // This prevents the individual drag delay before group drag kicks in
          const node = e.target;
          const isInMultiSelection = selectedObjects && selectedObjects.length > 1 &&
            selectedObjects.some((obj) => obj.id === rectangle.id && obj.type === "Rect");

          if (isInMultiSelection && onDragMove) {
            // Immediately trigger group drag by calling onDragMove with current position
            // This ensures group drag starts immediately without individual element movement
            // CRITICAL: Pass the element attributes in the format expected by onDragMove
            const currentX = node.x();
            const currentY = node.y();
            
            onDragMove?.(
              {
                ...rectangle,
                id: rectangle.id,
                x: currentX,
                y: currentY,
                width: rectangle.width,
                height: rectangle.height,
                text: rectangle.text,
              },
              e
            );
          }

          crossStageGestureBegin?.(
            typeof e.target?.getStage === "function"
              ? e.target.getStage()
              : null,
            e.evt,
          );

          if (isOffCanvas && e.target) {
            const st = e.target.getStage?.();
            const sw = st?.width?.() ?? 0;
            if (sw > 0) {
              const ext = getAbsoluteHorizontalExtent(
                rectangle,
                "Rect",
                e.target.x(),
              );
              offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
                ext.minX,
                ext.maxX,
                sw,
              );
            } else {
              offCanvasDragStripSideRef.current = null;
            }
          } else {
            offCanvasDragStripSideRef.current = null;
          }
        }}
        onDragMove={(e) => {
          // CRITICAL SAFETY CHECK: Only allow drag if mouse button is actually pressed
          if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
            const node = shapeRef.current;
            if (node) {
              node.stopDrag();
            }
            return;
          }

          const node = shapeRef.current;
          if (!node) return;

          // CRITICAL: If we just double-clicked, stop the drag immediately
          if (justDoubleClickedRef.current) {
            node.stopDrag();
            // Reset to original position
            if (doubleClickOriginalPositionRef.current.x !== null) {
              node.x(doubleClickOriginalPositionRef.current.x);
              node.y(doubleClickOriginalPositionRef.current.y);
            }
            // Disable dragging to prevent it from continuing
            node.setDraggable(false);
            // Re-enable after a delay
            setTimeout(() => {
              if (node && allowDrag) {
                node.setDraggable(true);
              }
            }, 100);
            return;
          }

          let newX = node.x();
          let newY = node.y();

          // CRITICAL FIX: Update text Group position immediately during drag for smooth text movement
          // This prevents text lag by synchronizing text position with shape position in real-time
          if (textGroupRef.current) {
            const textDimensions = getTextContainerDimensions();
            textGroupRef.current.x(newX + textDimensions.padding);
            textGroupRef.current.y(newY + textDimensions.padding);

            // Force layer redraw for immediate visual update
            const layer = textGroupRef.current.getLayer();
            if (layer) {
              layer.batchDraw();
            }
          }

          // CRITICAL FIX: Convert coordinates when element crosses into off-canvas during drag move
          // This ensures RectangleEditor shows the same coordinates as drag end
          const mainCanvasWidth = window.innerWidth * 0.45;
          const hasCrossedIntoOffCanvas = !isOffCanvas && newX >= mainCanvasWidth;

          let displayX = newX;
          let displayY = newY;

          if (hasCrossedIntoOffCanvas) {
            // Element has crossed into off-canvas but is still on main canvas stage
            // Convert coordinates to off-canvas stage coordinates (same as drag end)
            const elementStage = node.getStage();
            if (elementStage) {
              const sourceBBox = elementStage.container().getBoundingClientRect();

              // Get client coordinates from the event if available, otherwise calculate from node position
              let clientX, clientY;
              if (e.evt) {
                clientX = e.evt.clientX ?? e.evt.touches?.[0]?.clientX;
                clientY = e.evt.clientY ?? e.evt.touches?.[0]?.clientY;
              }

              // If event doesn't have client coordinates, calculate from node position
              if (clientX === undefined || clientY === undefined) {
                clientX = sourceBBox.left + newX;
                clientY = sourceBBox.top + newY;
              }

              // Try to get off-canvas stage bounding box
              // Off-canvas stage container can be found by looking for the off-canvas stage container
              let offBBox = null;

              // Method 1: Try to find off-canvas stage via window ref
              if (window.offCanvasStageRef?.current) {
                const offCanvasStage = window.offCanvasStageRef.current;
                offBBox = offCanvasStage.container()?.getBoundingClientRect();
              }

              // Method 2: Try to find by checking all Konva stages
              if (!offBBox) {
                const allCanvases = document.querySelectorAll('canvas');
                for (let canvas of allCanvases) {
                  // Check if this canvas belongs to a Konva stage
                  const stage = canvas.getStage?.();
                  if (stage && stage !== elementStage) {
                    const stageWidth = stage.width();
                    // Off-canvas stage is typically full width (window.innerWidth)
                    if (stageWidth && Math.abs(stageWidth - window.innerWidth) < 10) {
                      offBBox = stage.container()?.getBoundingClientRect();
                      if (offBBox) break;
                    }
                  }
                }
              }

              // Method 3: Calculate approximate off-canvas bounding box
              // Off-canvas stage typically starts at mainCanvasWidth from viewport left
              if (!offBBox) {
                // Approximate: off-canvas stage starts where main canvas ends
                offBBox = {
                  left: sourceBBox.left + mainCanvasWidth,
                  top: sourceBBox.top,
                };
              }

              if (offBBox) {
                // Convert from client coordinates to off-canvas stage coordinates
                displayX = clientX - offBBox.left;
                displayY = clientY - offBBox.top;
              }
            }
          }

          // Pass stage-native x/y to the renderer: main-stage elements must stay in main space so
          // Canvas/RectangleEditor show main coordinates while the pointer previews off-canvas.
          onDragMove?.(
            {
              ...rectangle,
              id: rectangle.id,
              x: isOffCanvas ? displayX : newX,
              y: isOffCanvas ? displayY : newY,
              width: rectangle.width,
              height: rectangle.height,
              rotation: rectangle.rotation || 0,
              text: rectangle.text,
            },
            e
          );
        }}
        onDragEnd={(e) => {
          crossStageGestureEnd?.();
          const node = e.target;
          // Match CircleElement behavior: round final coordinates before passing to dragEnd utils
          const newX = Math.round(node.x());
          const newY = Math.round(node.y());
          // Get mouse coordinates from the event for group drag detection
          // Try multiple ways to get mouse coordinates as Konva event structure may vary
          const mouseClientX =
            e.evt?.clientX ??
            e.clientX ??
            e.evt?.changedTouches?.[0]?.clientX;
          const mouseClientY =
            e.evt?.clientY ??
            e.clientY ??
            e.evt?.changedTouches?.[0]?.clientY;

          offCanvasDragStripSideRef.current = null;
          onDragEnd(rectangle.id, newX, newY, mouseClientX, mouseClientY);
        }}
        onTransformStart={(e) => {
          transformActiveAnchorRef.current = null;
          const trNode = e?.target?.getLayer?.()?.findOne?.("Transformer");
          if (trNode && typeof trNode.getActiveAnchor === "function") {
            transformActiveAnchorRef.current = trNode.getActiveAnchor();
          }
          if (shapeRef.current) {
            const node = shapeRef.current;
            transformLastValidRef.current = {
              x: node.x(),
              y: node.y(),
              width: node.width(),
              height: node.height(),
              scaleX: node.scaleX(),
              scaleY: node.scaleY(),
              rotation: node.rotation(),
            };
          } else {
            transformLastValidRef.current = null;
          }
          if (!isOffCanvas || !shapeRef.current) return;
          const node = shapeRef.current;
          const st = node.getStage?.();
          const sw = st?.width?.() ?? 0;
          if (sw > 0) {
            const ext = getAbsoluteHorizontalExtent(
              { ...rectangle, width: node.width(), height: node.height() },
              "Rect",
              node.x(),
            );
            offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
              ext.minX,
              ext.maxX,
              sw,
            );
          } else {
            offCanvasDragStripSideRef.current = null;
          }
        }}
        onTransform={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          // Allow free transform beyond stage; clamp only on transform-end.
          const newWidth = Math.max(MIN_WIDTH, node.width() * scaleX);
          const newHeight = Math.max(MIN_HEIGHT, node.height() * scaleY);

          let rotation = node.rotation();
          // Normalize rotation to 0-360 range
          rotation = rotation % 360;
          if (rotation < 0) {
            rotation += 360;
          }

          node.scaleX(1);
          node.scaleY(1);

          node.width(newWidth);
          node.height(newHeight);

          // Calculate the correct position for the callback
          const callbackX = node.x();
          const callbackY = node.y();

          onTransform?.({
            ...rectangle,
            id: rectangle.id,
            x: callbackX,
            y: callbackY,
            width: newWidth,
            height: newHeight,
            rotation: rotation,
            text: rectangle.text,
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          const stage = node.getStage?.();

          const stageWidth =
            typeof stage?.width === "function" ? stage.width() : stage?.width;
          const stageHeight =
            typeof stage?.height === "function" ? stage.height() : stage?.height;

          let finalWidth = Math.max(MIN_WIDTH, node.width() * scaleX);
          let finalHeight = Math.max(MIN_HEIGHT, node.height() * scaleY);
          const startX = node.x();
          const startY = node.y();
          const preTransformX = transformLastValidRef.current?.x ?? startX;
          const preTransformY = transformLastValidRef.current?.y ?? startY;

          let rotation = node.rotation();
          // Normalize rotation to 0-360 range
          rotation = rotation % 360;
          if (rotation < 0) {
            rotation += 360;
          }

          const previousRotation = normalizeRotationDeg(
            transformLastValidRef.current?.rotation ?? rotation,
          );
          const currentRotation = normalizeRotationDeg(rotation);
          const didRotateInThisTransform =
            Math.abs(currentRotation - previousRotation) > 0.01;
          const usedRotaterHandle =
            transformActiveAnchorRef.current === "rotater";

          // Resize-only path: apply rotation-aware size caps.
          // Rotation path should NOT shrink width/height; it should revert rotation
          // if requested rotation cannot fit after position clamp.
          if (
            !(didRotateInThisTransform || usedRotaterHandle) &&
            typeof stageWidth === "number" &&
            Number.isFinite(stageWidth) &&
            stageWidth > 0 &&
            typeof stageHeight === "number" &&
            Number.isFinite(stageHeight) &&
            stageHeight > 0
          ) {
            const inset = TRANSFORMER_ANCHOR_BUFFER_PX;
            const { mainZoneStart } = isOffCanvas
              ? getOffCanvasMainColumnBounds(stageWidth)
              : { mainZoneStart: stageWidth };
            const availableProjectedW = Math.max(
              MIN_WIDTH,
              (isOffCanvas ? mainZoneStart : stageWidth) - inset * 2,
            );
            const availableProjectedH = Math.max(
              MIN_HEIGHT,
              stageHeight - inset * 2,
            );
            const theta = (rotation * Math.PI) / 180;
            const absCos = Math.abs(Math.cos(theta));
            const absSin = Math.abs(Math.sin(theta));
            const EPS = 1e-6;
            const clampToRotationAwareFit = (requestedW, requestedH) => {
              let w = Math.max(MIN_WIDTH, Number(requestedW) || MIN_WIDTH);
              let h = Math.max(MIN_HEIGHT, Number(requestedH) || MIN_HEIGHT);

              const maxWFromHoriz =
                absCos > EPS
                  ? (availableProjectedW - h * absSin) / absCos
                  : Number.POSITIVE_INFINITY;
              const maxWFromVert =
                absSin > EPS
                  ? (availableProjectedH - h * absCos) / absSin
                  : Number.POSITIVE_INFINITY;
              const maxW = Math.max(
                MIN_WIDTH,
                Math.min(maxWFromHoriz, maxWFromVert),
              );
              w = Math.max(MIN_WIDTH, Math.min(w, maxW));

              const maxHFromHoriz =
                absSin > EPS
                  ? (availableProjectedW - w * absCos) / absSin
                  : Number.POSITIVE_INFINITY;
              const maxHFromVert =
                absCos > EPS
                  ? (availableProjectedH - w * absSin) / absCos
                  : Number.POSITIVE_INFINITY;
              const maxH = Math.max(
                MIN_HEIGHT,
                Math.min(maxHFromHoriz, maxHFromVert),
              );
              h = Math.max(MIN_HEIGHT, Math.min(h, maxH));

              return { width: w, height: h };
            };
            const fitted = clampToRotationAwareFit(finalWidth, finalHeight);
            if (isRectTransformClampDebugEnabled) {
            }
            finalWidth = fitted.width;
            finalHeight = fitted.height;
          }

          node.scaleX(1);
          node.scaleY(1);
          node.width(finalWidth);
          node.height(finalHeight);
          node.rotation(rotation);
          // Ensure Konva recomputes geometry before we read getClientRect for clamping.
          // Without this, client-rect can briefly reflect the pre-transform bounds.
          node.getLayer?.()?.batchDraw?.();

          // Calculate the correct position for the callback
          let callbackX = node.x();
          let callbackY = node.y();
          if (stage) {
            const stageWidth = stage.width() || window.innerWidth * 0.45;
            const stageHeight = stage.height() || window.innerHeight;
            const applyStagePositionClamp = () =>
              clampNodePositionInsideStageByClientRect(
                node,
                stageWidth,
                stageHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );
            const isCurrentClientRectInsideStage = () =>
              isClientRectInsideStage(
                stage
                  ? node.getClientRect({ relativeTo: stage })
                  : node.getClientRect(),
                stageWidth,
                stageHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );

            if (didRotateInThisTransform || usedRotaterHandle) {
              const requestedRotation = currentRotation;
              node.rotation(requestedRotation);
              const shiftedAtRequestedRotation = applyStagePositionClamp();
              const requestedRotationFits = isCurrentClientRectInsideStage();

              if (requestedRotationFits) {
                rotation = requestedRotation;
                callbackX = shiftedAtRequestedRotation.x;
                callbackY = shiftedAtRequestedRotation.y;
              } else {
                // Requested rotation cannot fit even after pushing x/y inward;
                // revert to previous rotation instead of forcing a different angle.
                rotation = previousRotation;
                node.rotation(rotation);
                const shiftedAtPreviousRotation = applyStagePositionClamp();
                callbackX = shiftedAtPreviousRotation.x;
                callbackY = shiftedAtPreviousRotation.y;
                // Only on rejected rotation fallback: restore original X from transform start.
                callbackX = preTransformX;
                node.x(callbackX);
              }

              if (isRectTransformClampDebugEnabled) {
              }
            } else {
              // Preserve user rotation on resize-only transforms.
              node.rotation(rotation);
              const shifted = applyStagePositionClamp();
              callbackX = shifted.x;
              callbackY = shifted.y;
            }

            if (isRectTransformClampDebugEnabled) {
              const stageRect = node.getClientRect({ relativeTo: stage });
            }
          }

          if (isOffCanvas) {
            if (stage) {
              const isRotationInteraction =
                didRotateInThisTransform || usedRotaterHandle;
              if (isRotationInteraction) {
                const stageW = stage.width() || window.innerWidth;
                const stageH = stage.height() || window.innerHeight;
                // Current x at transform end (after Konva interaction).
                // Use this as the primary "keep position" anchor when requested
                // rotation fits, so we don't snap back to transform-start x.
                const lockedX = startX;
                // Previous x from transform start (used only for fallback/revert).
                const fallbackX = preTransformX;
                const requestedRotation = currentRotation;
                const applyOffCanvasClamp = () =>
                  clampNodeInsideOffCanvasStripsByClientRect(
                    node,
                    stageW,
                    stageH,
                    TRANSFORMER_ANCHOR_BUFFER_PX,
                    { clampX: true, clampY: true }
                  );
                // Reuse main-canvas fit decision semantics: decide fallback based on
                // stage-fit only, then apply off-canvas strip/seam clamp as a separate step.
                const isCurrentStageFit = () =>
                  isClientRectInsideStage(
                    node.getClientRect({ relativeTo: stage }),
                    stageW,
                    stageH,
                    TRANSFORMER_ANCHOR_BUFFER_PX
                  );

                node.rotation(requestedRotation);
                // Prefer preserving x from transform start when requested rotation already fits there.
                node.x(lockedX);
                const requestedFitsAtLockedX = isCurrentStageFit();
                const clampedRequested = applyOffCanvasClamp();
                const requestedFitsStrict = isCurrentStageFit();
                const requestedRect = node.getClientRect({ relativeTo: stage });
                const { mainZoneStart } = getOffCanvasMainColumnBounds(stageW);
                const insetX = getEffectiveInset(
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                  stageW,
                  requestedRect.width,
                );
                // Treat as fit when rendered rotated width can physically fit in the left strip.
                // This avoids false fallback/revert due seam precision jitter near main-zone boundary.
                const canFitLeftStripByWidth =
                  requestedRect.width <= Math.max(0, mainZoneStart - insetX * 2 + 1);
                const requestedFits = requestedFitsStrict || canFitLeftStripByWidth;

                if (requestedFitsAtLockedX) {
                  rotation = requestedRotation;
                  callbackX = lockedX;
                  callbackY = node.y();
                } else if (requestedFits) {
                  rotation = requestedRotation;
                  callbackX = clampedRequested.x;
                  callbackY = clampedRequested.y;
                } else {
                  rotation = previousRotation;
                  node.rotation(rotation);
                  node.x(fallbackX);
                  const clampedPrevious = applyOffCanvasClamp();
                  callbackX = clampedPrevious.x;
                  callbackY = clampedPrevious.y;
                }
                node.x(callbackX);
                node.y(callbackY);
                if (isRectTransformClampDebugEnabled) {
                }
                offCanvasDragStripSideRef.current = null;
                onTransformEnd?.({
                  ...rectangle,
                  id: rectangle.id,
                  x: callbackX,
                  y: callbackY,
                  width: finalWidth,
                  height: finalHeight,
                  rotation: rotation,
                  text: rectangle.text,
                });
                transformLastValidRef.current = null;
                transformActiveAnchorRef.current = null;
                return;
              }

              const merged = {
                ...rectangle,
                x: callbackX,
                y: callbackY,
                width: finalWidth,
                height: finalHeight,
              };
              const pos = constrainOffCanvasPosition(
                merged,
                "Rect",
                callbackX,
                callbackY,
                stage,
                offCanvasDragStripSideRef.current,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );
              callbackX = pos.constrainedX;
              callbackY = pos.constrainedY;
              node.x(callbackX);
              node.y(callbackY);
              node.getLayer?.()?.batchDraw?.();
              const stripClamped = clampNodeInsideOffCanvasStripsByClientRect(
                node,
                stage.width() || window.innerWidth,
                stage.height() || window.innerHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );
              callbackX = stripClamped.x;
              callbackY = stripClamped.y;
              if (isRectTransformClampDebugEnabled) {
              }
            }
          }
          offCanvasDragStripSideRef.current = null;

          onTransformEnd?.({
            ...rectangle,
            id: rectangle.id,
            x: callbackX,
            y: callbackY,
            width: finalWidth,
            height: finalHeight,
            rotation: rotation,
            text: rectangle.text,
          });
          transformLastValidRef.current = null;
          transformActiveAnchorRef.current = null;
        }}
        stroke={applyTransparency(
          strokeColor || "#ffffff",
          rectangle.strokeOpacity ?? 1
        )}
        strokeWidth={getEffectiveStrokeWidth()}
        strokeScaleEnabled={false}
        strokeEnabled
        strokeOpacity={rectangle.strokeOpacity !== undefined ? rectangle.strokeOpacity : 1}
        perfectDrawEnabled={true} // Enable perfect drawing for crisp edges
        shadowForStrokeEnabled={false} // Disable shadow for stroke to prevent fading
        fill={applyTransparency(
          rectangle.fill || rectangle.fillColor || "#ffffff",
          rectangle.fillOpacity ?? 1
        )}
        opacity={rectangle.opacity !== undefined ? rectangle.opacity : 1}
        listening={true}
      />

      {/* Copy button commented out */}
      {false && shouldShowCopyButton && (
        <Html>
          <div
            style={{
              position: "absolute",
              top: `${copyButtonTop}px`,
              left: `${copyButtonLeft}px`,
              pointerEvents: "none",
              zIndex: 30,
            }}
          >
            <button
              type="button"
              onClick={handleCopyClick}
              title={
                isOffCanvas ? "Copy to Main Canvas" : "Copy to Off-Canvas Space"
              }
              style={{
                width: "26px",
                height: "26px",
                borderRadius: "4px",
                border: "1px solid #CBD5E1",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                cursor: "pointer",
                pointerEvents: "auto",
                padding: 0,
                transition:
                  "background-color 0.15s ease, border-color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F1F5F9";
                e.currentTarget.style.borderColor = "#94A3B8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#FFFFFF";
                e.currentTarget.style.borderColor = "#CBD5E1";
              }}
            >
              <svg
                width="16"
                height="19"
                viewBox="0 0 17 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                  fill="#1E293B"
                />
              </svg>
            </button>
          </div>
        </Html>
      )}

      {/* Clipped Group for text */}
      {textContent &&
        (() => {
          const textDimensions = getTextContainerDimensions();

          // CRITICAL FIX: Always read position from shape ref for smooth text movement during drag
          // The ref gives us the real-time position, while props only update on React re-render
          // This prevents text lag during drag operations
          let currentX = rectangle.x;
          let currentY = rectangle.y;
          let currentRotation = rotation;

          // Check if this element is part of a group drag (multiple selected objects)
          const isInGroupDrag = isInGroupDragRef.current;

          // CRITICAL PERFORMANCE FIX: Use cached HTML content during group drag to prevent expensive re-renders
          // During group drag, only Konva node positions are updated via requestAnimationFrame,
          // not the HTML content. This prevents lag when dragging elements with large HTML content.
          const htmlContentToRender = isInGroupDrag
            ? htmlContentRef.current
            : (textContent || "");

          // CRITICAL FIX: Always use ref position if available for smooth text movement
          // This ensures text follows the shape instantly during drag, not just during group drag
          if (shapeRef.current) {
            try {
              // Always read from ref to get real-time position during any interaction
              // This prevents text lag during single element drag
              currentX = shapeRef.current.x();
              currentY = shapeRef.current.y();
              currentRotation = shapeRef.current.rotation();
            } catch (e) {
              // Fallback to props if ref access fails
              currentX = rectangle.x;
              currentY = rectangle.y;
              currentRotation = rotation;
            }
          }

          return (
            <Group
              ref={textGroupRef}
              key={`text-group-${rectangle.id}-${renderKey}-${rectangle.x}-${rectangle.y}`}
              x={currentX + textDimensions.padding}
              y={currentY + textDimensions.padding}
              width={textDimensions.width}
              height={textDimensions.height}
              scaleX={1}
              scaleY={1}
              rotation={currentRotation}
              offsetX={0}
              offsetY={0}
              listening={false}
              clipFunc={(ctx) => {
                ctx.beginPath();

                // Canvas boundaries (use effective height to account for resize handle)
                const canvasLeft = 0;
                const canvasTop = 0;
                const canvasRight = canvasPixelWidth;
                const canvasBottom = effectiveCanvasHeight;

                // Calculate the rectangle boundaries (top-left aligned)
                const rectLeft = currentX + textDimensions.padding;
                const rectTop = currentY + textDimensions.padding;
                const rectRight = rectLeft + textDimensions.width;
                const rectBottom = rectTop + textDimensions.height;

                // Calculate intersection with canvas boundaries
                const clipLeft = Math.max(canvasLeft, rectLeft);
                const clipTop = Math.max(canvasTop, rectTop);
                const clipRight = Math.min(canvasRight, rectRight);
                const clipBottom = Math.min(canvasBottom, rectBottom);

                // If element is completely outside canvas, don't render it
                if (clipLeft >= clipRight || clipTop >= clipBottom) {
                  ctx.rect(0, 0, 0, 0); // Empty clip
                  ctx.closePath();
                  return;
                }

                // For rotated rectangles, we need to combine canvas boundary clipping
                // with rectangle shape clipping
                // Since Group is positioned at top-left + padding, corners are relative to (0, 0)
                const corners = [
                  { x: 0, y: 0 }, // Top-left
                  { x: textDimensions.width, y: 0 }, // Top-right
                  { x: textDimensions.width, y: textDimensions.height }, // Bottom-right
                  { x: 0, y: textDimensions.height }, // Bottom-left
                ];

                // Apply rotation to each corner
                const angle = (rotation * Math.PI) / 180;
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);

                const rotatedCorners = corners.map((corner) => ({
                  x: corner.x * cos - corner.y * sin,
                  y: corner.x * sin + corner.y * cos,
                }));

                // Create a clipping path that follows the rotated rectangle
                ctx.moveTo(rotatedCorners[0].x, rotatedCorners[0].y);
                for (let i = 1; i < rotatedCorners.length; i++) {
                  ctx.lineTo(rotatedCorners[i].x, rotatedCorners[i].y);
                }
                ctx.closePath();
                ctx.clip();

                // Now apply canvas boundary clipping in local coordinates
                // Convert canvas boundaries to local Group coordinates
                const localLeft = clipLeft - rectLeft;
                const localTop = clipTop - rectTop;
                const localWidth = clipRight - clipLeft;
                const localHeight = clipBottom - clipTop;

                // Apply second clipping for canvas boundaries
                // Since Group is positioned at top-left, local coordinates start at (0, 0)
                ctx.beginPath();
                ctx.rect(
                  localLeft,
                  localTop,
                  localWidth,
                  localHeight
                );
                ctx.closePath();
              }}
            >
              {/* Render text with HTML formatting */}
              <Html
                divProps={{
                  "data-rectangle-id": rectangle.id,
                  "data-behind-selected": isHtmlContentBehindSelected()
                    ? "true"
                    : "false",
                  style: {
                    pointerEvents: "none",
                    zIndex: isSelected
                      ? 15
                      : isHtmlContentBehindSelected()
                        ? 1
                        : 3, // Selected elements on top (15), behind selected (1), non-selected below (3)
                    ...getHtmlMask(), // Use mask-image to hide overlapping portions when behind selected elements
                    // The polygon approach works for TextElement, so it should work here too
                    position: "absolute", // Ensure z-index works
                    // CRITICAL: Clip content that extends outside canvas boundaries (only for main canvas)
                    ...(isOffCanvas ? {} : (() => {
                      // Calculate text container position
                      const rectLeft = currentX + textDimensions.padding;
                      const rectTop = currentY + textDimensions.padding;
                      const rectRight = rectLeft + textDimensions.width;
                      const rectBottom = rectTop + textDimensions.height;

                      // Calculate overflow using full text element bounds (like TextElement)
                      const overflowLeft = Math.max(0, -rectLeft);
                      const overflowTop = Math.max(0, -rectTop);
                      const overflowRight = Math.max(0, rectRight - canvasPixelWidth);
                      const overflowBottom = Math.max(0, rectBottom - effectiveCanvasHeight);

                      // If any overflow exists, apply clipping
                      if (overflowLeft > 0 || overflowTop > 0 || overflowRight > 0 || overflowBottom > 0) {
                        // Use full width/height - let clip-path handle clipping (like TextElement)
                        // clip-path inset() takes: top, right, bottom, left (distances from edges)
                        return {
                          overflow: "hidden",
                          clipPath: `inset(${overflowTop}px ${overflowRight}px ${overflowBottom}px ${overflowLeft}px)`,
                          width: `${textDimensions.width}px`, // Use full width
                          height: `${textDimensions.height}px`, // Use full height
                        };
                      }
                      // CRITICAL: Explicitly remove clipping when element is fully inside canvas
                      return {
                        overflow: "visible",
                        clipPath: "none",
                        width: `${textDimensions.width}px`, // Use full width
                        height: `${textDimensions.height}px`, // Use full height
                      };
                    })()),
                  },
                }}
              >
                <div
                  style={{
                    width: `${textDimensions.width}px`, // Use full width - let container clip-path handle clipping
                    height: `${textDimensions.height}px`, // Use full height - let container clip-path handle clipping
                    display: "flex",
                    alignItems:
                      verticalAlign === "top"
                        ? "flex-start"
                        : verticalAlign === "bottom"
                          ? "flex-end"
                          : "center",
                    justifyContent:
                      align === "left"
                        ? "flex-start"
                        : align === "right"
                          ? "flex-end"
                          : "center",
                    padding: "0px", // Remove all padding to eliminate vertical gaps
                    margin: "0px", // Remove all margins
                    overflow: "hidden", // Critical: Hide text that extends beyond container
                    pointerEvents: "none", // Allow drag events to pass through
                    cursor: "default",
                    userSelect: "none", // Prevent text selection
                    WebkitUserSelect: "none", // Safari
                    MozUserSelect: "none", // Firefox
                    msUserSelect: "none", // IE/Edge
                    backgroundColor: "transparent", // Ensure no background color
                    border: "none", // Ensure no border
                    outline: "none", // Ensure no outline
                    boxSizing: "border-box", // Ensure proper box model
                    visibility: (() => {
                      // Calculate if rectangle is visible within canvas boundaries
                      // Text container is positioned at top-left + padding
                      const rectTop = currentY + textDimensions.padding;
                      const rectBottom = rectTop + textDimensions.height;

                      // Hide text completely if rectangle is entirely outside canvas
                      if (rectTop >= effectiveCanvasHeight || rectBottom <= 0) {
                        return "hidden";
                      }
                      return "visible";
                    })(),
                    clipPath: "none",
                    WebkitClipPath: "none",
                  }}
                >
                  {/* Inner content div with ellipsis */}
                  <div
                    className="circle-text-content"
                    data-export="true"
                    data-content-type="rectangle"
                    style={{
                      fontFamily: "Roboto",
                      fontSize: `${fontSize}px`,
                      fontWeight: fontStyle.includes("bold")
                        ? "bold"
                        : "normal",
                      fontStyle: fontStyle.includes("italic")
                        ? "italic"
                        : "normal",
                      textDecoration: textDecoration,
                      color: textColor,
                      textAlign: align,
                      lineHeight: "1em",
                      wordWrap: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      backgroundColor: "transparent", // Ensure no background
                      border: "none", // Ensure no border
                      outline: "none", // Ensure no outline
                      WebkitLineClamp: (() => {
                        // Recalculate when fontSize changes - this ensures fresh calculation on font size updates
                        // Start with theoretical maximum lines
                        const maxPossibleLines = Math.max(
                          1,
                          Math.floor(textDimensions.height / fontSize)
                        );

                        // Space needed for descenders (g, p, q, y) and ellipsis
                        // Reduced values to minimize vertical padding
                        const descenderSpace = Math.ceil(fontSize * 0.25); // Reduced from 0.35
                        const ellipsisSpace = Math.ceil(fontSize * 0.3); // Reduced from 0.45
                        const extraSpaceNeeded = descenderSpace + ellipsisSpace;

                        // Reduced safety buffer to minimize vertical padding
                        const safetyBuffer = Math.max(
                          1,
                          Math.ceil(fontSize * 0.05)
                        ); // Reduced from 3px/10% to 1px/5%
                        const availableHeight =
                          textDimensions.height - safetyBuffer;

                        // Check each line count from max down to 1 to find what actually fits
                        let safeLineCount = 1;
                        for (
                          let lines = maxPossibleLines;
                          lines >= 1;
                          lines--
                        ) {
                          // Calculate total height needed: lines + descenders + ellipsis
                          const totalHeightNeeded =
                            lines * fontSize + extraSpaceNeeded;

                          // If this fits within available height (with safety buffer), use it
                          if (totalHeightNeeded <= availableHeight) {
                            safeLineCount = lines;
                            break;
                          }
                        }

                        // Always return at least 1 to show ellipsis even if content is partially cut
                        return safeLineCount;
                      })(),
                      WebkitBoxOrient: "vertical",
                      whiteSpace: "pre-wrap", // Preserve consecutive line breaks
                      width: "100%", // Match outer container width to prevent overflow
                      maxWidth: "100%", // Ensure it doesn't exceed container
                      boxSizing: "border-box", // Ensure no padding/border adds to size
                      maxHeight: `${(() => {
                        // Calculate maxHeight based on safe line count to prevent cutting
                        // Match WebkitLineClamp calculation exactly
                        const maxPossibleLines = Math.max(
                          1,
                          Math.floor(textDimensions.height / fontSize)
                        );
                        const descenderSpace = Math.ceil(fontSize * 0.25); // Reduced from 0.35
                        const ellipsisSpace = Math.ceil(fontSize * 0.3); // Reduced from 0.45
                        const extraSpaceNeeded = descenderSpace + ellipsisSpace;

                        // Reduced safety buffer (same as WebkitLineClamp)
                        const safetyBuffer = Math.max(
                          1,
                          Math.ceil(fontSize * 0.05)
                        ); // Reduced from 3px/10% to 1px/5%
                        const availableHeight =
                          textDimensions.height - safetyBuffer;

                        // Find safe line count (same logic as WebkitLineClamp)
                        let safeLineCount = 1;
                        for (
                          let lines = maxPossibleLines;
                          lines >= 1;
                          lines--
                        ) {
                          const totalHeightNeeded =
                            lines * fontSize + extraSpaceNeeded;
                          if (totalHeightNeeded <= availableHeight) {
                            safeLineCount = lines;
                            break;
                          }
                        }

                        // Return the actual height needed for safe lines
                        // Use full container height to minimize padding
                        const calculatedHeight =
                          safeLineCount * fontSize + extraSpaceNeeded;
                        // Use the calculated height directly to minimize vertical padding
                        return calculatedHeight;
                      })()}px`,
                      margin: "0",
                      padding: "0",
                      pointerEvents: "none",
                      userSelect: "none",
                      opacity: getHtmlOpacity(),
                    }}
                    dangerouslySetInnerHTML={{
                      __html: htmlContentToRender,
                    }}
                  />
                </div>
              </Html>
            </Group>
          );
        })()}
    </>
  );
};

export default RectangleElement;
